import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MessageSquare, HelpCircle, FileText, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="support-page">
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FendanaCura" className="h-8 w-auto" />
            <span className="text-lg font-heading font-bold text-foreground">FendanaCura</span>
          </Link>
          <Link to="/"><Button variant="ghost" size="sm" className="text-muted-foreground"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button></Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">Support Centre</h1>
          <p className="text-muted-foreground mt-2">We&apos;re here to help. Find answers to common questions or get in touch with our team.</p>
        </div>

        {/* Contact */}
        <section className="space-y-4">
          <h2 className="text-lg font-heading font-semibold text-foreground">Contact Us</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <a href="mailto:customerservices@fendanahealth.com" className="flex items-center gap-4 p-5 rounded-xl bg-muted border border-border hover:border-[#00A6A0]/40 transition-colors">
              <div className="p-3 rounded-xl bg-[#00A6A0]/10"><Mail className="w-5 h-5 text-[#00A6A0]" /></div>
              <div>
                <p className="text-foreground font-medium text-sm">Email Support</p>
                <p className="text-xs text-muted-foreground">customerservices@fendanahealth.com</p>
              </div>
            </a>
            <a href="tel:+447000000000" className="flex items-center gap-4 p-5 rounded-xl bg-muted border border-border hover:border-[#FF6F61]/40 transition-colors">
              <div className="p-3 rounded-xl bg-[#FF6F61]/10"><Phone className="w-5 h-5 text-[#FF6F61]" /></div>
              <div>
                <p className="text-foreground font-medium text-sm">Phone Support</p>
                <p className="text-xs text-muted-foreground">Available Mon–Fri, 9am–5pm GMT</p>
              </div>
            </a>
          </div>
        </section>

        {/* FAQ */}
        <section className="space-y-4">
          <h2 className="text-lg font-heading font-semibold text-foreground">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {[
              { q: 'How do I reset my password?', a: 'Go to the login page and click "Forgot Password". Enter your registered email and follow the instructions sent to your inbox.' },
              { q: 'How do I invite caregivers to my agency?', a: 'As an Agency Admin, go to your Dashboard and click "Invite Staff". Enter their email and select the role. They will receive an invitation link.' },
              { q: 'How do I upload compliance documents?', a: 'Caregivers can upload documents via Document Storage > My Docs tab. Admins can upload from the All Files tab. All uploads support PDF, images, and common document formats.' },
              { q: 'How do I sign off on training documents?', a: 'Navigate to Document Storage > Training Sign-Offs tab. Read the document, click "I have Read & Understood", then draw your digital signature to complete.' },
              { q: 'How does the subscription work?', a: 'FendanaCura offers tiered subscriptions (Starter, Growth, Professional, Enterprise). Visit the Billing page from your admin dashboard to manage your plan.' },
              { q: 'Is my data secure?', a: 'Yes. FendanaCura uses encrypted connections (TLS), secure cloud storage, role-based access control, and complies with data protection standards. Your data is never shared with third parties.' },
              { q: 'How do I contact my care team?', a: 'Use the Messages section in the app to communicate directly with caregivers, clients, and family members within your organization.' },
            ].map((faq, i) => (
              <details key={i} className="group rounded-xl border border-border bg-muted overflow-hidden">
                <summary className="flex items-center gap-3 p-4 cursor-pointer text-sm text-foreground font-medium list-none">
                  <HelpCircle className="w-4 h-4 text-[#00A6A0] flex-shrink-0" />
                  <span className="flex-1">{faq.q}</span>
                  <span className="text-muted-foreground text-xs group-open:rotate-180 transition-transform">&#9660;</span>
                </summary>
                <div className="px-4 pb-4 pl-11 text-sm text-muted-foreground">{faq.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* Quick Links */}
        <section className="space-y-4">
          <h2 className="text-lg font-heading font-semibold text-foreground">Quick Links</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/privacy"><Button variant="outline" size="sm" className="border-border text-muted-foreground"><Shield className="w-4 h-4 mr-1.5" />Privacy Policy</Button></Link>
            <Link to="/copyright"><Button variant="outline" size="sm" className="border-border text-muted-foreground"><FileText className="w-4 h-4 mr-1.5" />Terms &amp; Copyright</Button></Link>
            <Link to="/login"><Button variant="outline" size="sm" className="border-border text-muted-foreground"><MessageSquare className="w-4 h-4 mr-1.5" />Login</Button></Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-8 text-center">
        <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} FendanaCura by Fendana Health Ltd. All rights reserved.</p>
      </footer>
    </div>
  );
}
